import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { motion } from "framer-motion";
import { AddressIcon, PhoneIcon, EmailIcon, ClockIcon } from "../assets/svgs/ContactIcons";
import { ReactNode } from "react";
import { SeoHead } from '@/components/SeoHead';

interface SocialLink {
  icon: string;
  color: string;
  hover: string;
  url: string;
  label: string;
  iconComponent?: ReactNode;
}

export default function Contact() {
  return (
    <>
      <SeoHead 
        title="Contact Us | ZeeX Soft Software Development"
        description="Get in touch with our software development team in UAE. Let's discuss your web, mobile, or custom software project."
        keywords="contact software developers, UAE tech company, web development inquiry"
      />

      <SEO
        title="Contact ZeeX Soft - Get Your Free Quote Today"
        description="Contact ZeeX Soft for your software development needs. Located in Sharjah, UAE. Get a free quote for web development, mobile apps, and digital solutions."
        keywords="contact ZeeX Soft, software development quote UAE, web development Sharjah, free consultation"
      />

      {/* Hero Section */}
      <section className="relative pt-40 pb-28 overflow-hidden bg-gradient-to-b from-background to-muted/5">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 to-transparent"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        </div>
        <div className="container relative z-10 px-6 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest text-primary uppercase rounded-full bg-primary/10"
            >
              Let's Talk
            </motion.span>
            <motion.h1 
              className="mb-6 text-5xl font-bold md:text-6xl lg:text-7xl font-orbitron bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Get In Touch
            </motion.h1>
            <motion.p 
              className="max-w-3xl mx-auto text-xl md:text-2xl text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Ready to start your next project? Let's discuss how we can help architect your digital future.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-background to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-background to-transparent"></div>
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-secondary/5 blur-3xl"></div>
        </div>
        
        <div className="container px-6 mx-auto">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
              className="space-y-8"
            >
              <div className="p-8 rounded-2xl bg-gradient-to-br from-card to-card/90 border border-border/20 shadow-xl">
                <h3 className="font-orbitron font-bold text-2xl md:text-3xl mb-8 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                  Contact Information
                </h3>
                
                <div className="space-y-6">
                  <motion.div 
                    className="flex items-center space-x-6 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-purple-500/20 group-hover:scale-110 transition-transform">
                      <AddressIcon className="w-6 h-6 text-foreground/80" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">Address</p>
                      <p className="text-muted-foreground text-sm">Almawarid Building 209, Industrial Area 3, Sharjah, UAE</p>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-center space-x-6 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-green-400/20 to-cyan-500/20 group-hover:scale-110 transition-transform">
                      <PhoneIcon className="w-6 h-6 text-foreground/80" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">Phone</p>
                      <a href="tel:+447743307781" className="text-muted-foreground text-sm hover:text-primary transition-colors">+44 7743 307781</a>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-center space-x-6 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-pink-400/20 to-yellow-400/20 group-hover:scale-110 transition-transform">
                      <EmailIcon className="w-6 h-6 text-foreground/80" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">Email</p>
                      <a href="mailto:info@zeexsoft.com" className="text-muted-foreground text-sm hover:text-primary transition-colors">info@zeexsoft.com</a>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-center space-x-6 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-yellow-400/20 to-green-400/20 group-hover:scale-110 transition-transform">
                      <ClockIcon className="w-6 h-6 text-foreground/80" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">Business Hours</p>
                      <p className="text-muted-foreground text-sm">Sunday - Thursday: 9:00 AM - 6:00 PM</p>
                      <p className="text-muted-foreground text-sm">Friday - Saturday: Closed</p>
                    </div>
                  </motion.div>
                </div>
                
                <div className="mt-10">
                  <h4 className="font-semibold text-foreground mb-4">Connect With Us</h4>
                  <div className="flex flex-wrap gap-3">
                    {([
                      { 
                        icon: 'linkedin', 
                        color: 'from-blue-500/20 to-blue-600/20', 
                        hover: 'hover:bg-[#0077B5]', 
                        url: 'https://www.linkedin.com/company/zeexsoft/',
                        label: 'LinkedIn',
                        iconComponent: (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                        )
                      },
                      { 
                        icon: 'facebook', 
                        color: 'from-blue-600/20 to-blue-800/20', 
                        hover: 'hover:bg-[#1877F2]', 
                        url: 'https://www.facebook.com/zeexsoftofficial',
                        label: 'Facebook',
                        iconComponent: (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                          </svg>
                        )
                      },
                      { 
                        icon: 'whatsapp', 
                        color: 'from-green-500/20 to-green-600/20', 
                        hover: 'hover:bg-[#25D366]', 
                        url: 'https://api.whatsapp.com/send/?phone=447743307781',
                        label: 'WhatsApp',
                        iconComponent: (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M17.498 14.382l-.261-.367a.75.75 0 00-1.11-.123c-.117.1-1.22.995-2.84.995-1.62 0-2.722-.895-2.84-.995a.75.75 0 00-1.11.123l-.262.367c-.266.373-.35.84-.22 1.27.16.53.5.99.97 1.29.47.3 1.03.44 1.58.44.55 0 1.11-.14 1.58-.44.47-.3.81-.76.97-1.29.13-.43.046-.897-.22-1.27zM12 6.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9z" />
                            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.6.376 3.112 1.042 4.453l-1.02 3.055a1.5 1.5 0 001.828 1.829l3.055-1.02A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zM3.5 12c0-4.694 3.806-8.5 8.5-8.5 4.694 0 8.5 3.806 8.5 8.5s-3.806 8.5-8.5 8.5c-1.32 0-2.58-.3-3.707-.844l-3.202 1.07a.5.5 0 01-.61-.61l1.07-3.202A8.46 8.46 0 013.5 12z" clipRule="evenodd" />
                          </svg>
                        )
                      }
                    ] as SocialLink[]).map((social, index) => (
                      <motion.a
                        key={social.icon}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${social.color} backdrop-blur-sm border border-border/30 ${social.hover} hover:text-white transition-all`}
                        whileHover={{ y: -3, scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + (index * 0.1) }}
                        aria-label={social.label}
                      >
                        {social.iconComponent || <i className={`fab fa-${social.icon} text-lg`}></i>}
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
              
              <motion.div 
                className="overflow-hidden rounded-2xl border border-border/20 shadow-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="h-64 bg-muted/30 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <i className="fas fa-map-marker-alt text-primary"></i>
                    </div>
                    <h4 className="font-semibold text-foreground mb-2">Our Location</h4>
                    <p className="text-sm text-muted-foreground mb-4">Almawarid Building 209, Industrial Area 3, Sharjah, UAE</p>
                    <a 
                      href="https://maps.google.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                    >
                      View on map <i className="fas fa-external-link-alt ml-1 text-xs"></i>
                    </a>
                  </div>
                </div>
                <div className="p-6 bg-card/50 backdrop-blur-sm border-t border-border/20">
                  <h4 className="font-semibold text-foreground mb-3">Get Directions</h4>
                  <a 
                    href="https://www.google.com/maps/dir//Almawarid+Building+209,+Industrial+Area+3,+Sharjah,+UAE" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <span className="text-sm font-medium">Open in Google Maps</span>
                    <i className="fas fa-external-link-alt text-xs text-muted-foreground"></i>
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
              className="lg:sticky lg:top-8"
            >
              <div className="lg:pr-4">
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
